import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Alert,
  ActivityIndicator,
  Switch,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { ltcPreferencesService, LTCPreferencesData } from '../../../../services/ltcPreferencesInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('LTCPreferencesInput');

interface PreferenceOption {
  id: string;
  label: string;
}

type LTCPreferencesInputScreenRouteProp = RouteProp<RootStackParamList, 'LTCPreferencesInput'>;

interface CareTypePreferences {
  appliesToMe: boolean;
  selectedOptions: string[];
  additionalPreferences: string;
}

interface LocalLTCPreferencesData {
  inHomeCare: CareTypePreferences;
  adultDaycare: CareTypePreferences;
  assistedLiving: CareTypePreferences;
  memoryCare: CareTypePreferences;
  nursingHome: CareTypePreferences;
  hospice: CareTypePreferences;
  respiteCare: CareTypePreferences;
  continuedCare: CareTypePreferences;
}

const PREFERENCES_DATA = {
  inHomeCare: {
    title: 'In-Home Care',
    description: 'Professional caregivers provide assistance in your own home with daily activities, personal care, and companionship.',
    options: [
      { id: '1', label: 'Help with bathing and grooming' },
      { id: '2', label: 'Assistance with dressing' },
      { id: '3', label: 'Meal preparation and feeding' },
      { id: '4', label: 'Medication management and reminders' },
      { id: '5', label: 'Light housekeeping and laundry' },
      { id: '6', label: 'Companionship and social interaction' },
      { id: '7', label: 'Transportation to appointments' },
    ],
  },
  adultDaycare: {
    title: 'Adult Daycare',
    description: 'Supervised care during daytime hours in a community setting, offering social activities, meals, and basic health services.',
    options: [
      { id: '1', label: 'Social activities and recreation' },
      { id: '2', label: 'Therapeutic services (PT, OT)' },
      { id: '3', label: 'Meals and nutrition monitoring' },
      { id: '4', label: 'Health monitoring and medication supervision' },
      { id: '5', label: 'Transportation to and from facility' },
      { id: '6', label: 'Cognitive stimulation programs' },
    ],
  },
  assistedLiving: {
    title: 'Assisted Living',
    description: 'Residential community combining housing, personal care, and health services for those who need help with daily activities.',
    options: [
      { id: '1', label: 'Private room or apartment' },
      { id: '2', label: 'Shared accommodation with companion' },
      { id: '3', label: 'Close proximity to family (specific area)' },
      { id: '4', label: 'Pet-friendly facility' },
      { id: '5', label: 'Religious or spiritual services available' },
      { id: '6', label: 'Active social and recreational programs' },
      { id: '7', label: '24/7 staff availability' },
    ],
  },
  memoryCare: {
    title: 'Memory Care',
    description: 'Specialized care for individuals with Alzheimer\'s, dementia, or other memory impairments in a secure environment.',
    options: [
      { id: '1', label: 'Secure, monitored environment' },
      { id: '2', label: 'Memory-specific activities and therapies' },
      { id: '3', label: 'Trained staff in dementia care' },
      { id: '4', label: 'Structured daily routines' },
      { id: '5', label: 'Family involvement encouraged' },
      { id: '6', label: 'On-site medical support' },
    ],
  },
  nursingHome: {
    title: 'Nursing Home (Skilled Nursing)',
    description: '24/7 medical care and supervision for those with serious medical conditions or who need extensive daily assistance.',
    options: [
      { id: '1', label: '24/7 skilled nursing care' },
      { id: '2', label: 'Rehabilitation services (PT, OT, ST)' },
      { id: '3', label: 'Private room' },
      { id: '4', label: 'Semi-private or shared room' },
      { id: '5', label: 'Regular family visitation' },
      { id: '6', label: 'Specialized medical care (wound care, IV therapy)' },
    ],
  },
  hospice: {
    title: 'Hospice Care',
    description: 'Compassionate end-of-life care focusing on comfort, pain management, and quality of life for terminal illness.',
    options: [
      { id: '1', label: 'Hospice care at home' },
      { id: '2', label: 'Inpatient hospice facility' },
      { id: '3', label: 'Aggressive pain and symptom management' },
      { id: '4', label: 'Emotional and spiritual support for family' },
      { id: '5', label: 'Bereavement counseling for loved ones' },
      { id: '6', label: 'Religious or spiritual care services' },
    ],
  },
  respiteCare: {
    title: 'Respite Care',
    description: 'Temporary care relief for family caregivers, providing short-term assistance in home or facility settings.',
    options: [
      { id: '1', label: 'Short-term facility stay (days to weeks)' },
      { id: '2', label: 'In-home respite care' },
      { id: '3', label: 'Adult daycare for respite' },
      { id: '4', label: 'Emergency/unplanned respite available' },
      { id: '5', label: 'Regular scheduled respite care' },
      { id: '6', label: 'Overnight respite care' },
    ],
  },
  continuedCare: {
    title: 'Continuing Care (CCRC)',
    description: 'Continuing Care Retirement Communities offer multiple levels of care (independent, assisted, nursing) in one location.',
    options: [
      { id: '1', label: 'CCRC with independent living to nursing care' },
      { id: '2', label: 'Guaranteed care for life' },
      { id: '3', label: 'No need to relocate as needs change' },
      { id: '4', label: 'Community amenities (dining, fitness, social)' },
      { id: '5', label: 'Financial security and predictable costs' },
      { id: '6', label: 'Close to family or specific geographic area' },
    ],
  },
};

export const LTCPreferencesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<LTCPreferencesInputScreenRouteProp>();

  const { ltcPreferencesData, isEditing } = route.params || {};

  const [preferences, setPreferences] = useState<LocalLTCPreferencesData>({
    inHomeCare: {
      appliesToMe: ltcPreferencesData?.inHomeCareSelectedOptions ? ltcPreferencesData.inHomeCareSelectedOptions.length > 0 : true,
      selectedOptions: ltcPreferencesData?.inHomeCareSelectedOptions || [],
      additionalPreferences: ltcPreferencesData?.inHomeCareAdditionalPreferences || ''
    },
    adultDaycare: {
      appliesToMe: ltcPreferencesData?.adultDaycareSelectedOptions ? ltcPreferencesData.adultDaycareSelectedOptions.length > 0 : true,
      selectedOptions: ltcPreferencesData?.adultDaycareSelectedOptions || [],
      additionalPreferences: ltcPreferencesData?.adultDaycareAdditionalPreferences || ''
    },
    assistedLiving: {
      appliesToMe: ltcPreferencesData?.assistedLivingSelectedOptions ? ltcPreferencesData.assistedLivingSelectedOptions.length > 0 : true,
      selectedOptions: ltcPreferencesData?.assistedLivingSelectedOptions || [],
      additionalPreferences: ltcPreferencesData?.assistedLivingAdditionalPreferences || ''
    },
    memoryCare: {
      appliesToMe: ltcPreferencesData?.memoryCareSelectedOptions ? ltcPreferencesData.memoryCareSelectedOptions.length > 0 : true,
      selectedOptions: ltcPreferencesData?.memoryCareSelectedOptions || [],
      additionalPreferences: ltcPreferencesData?.memoryCareAdditionalPreferences || ''
    },
    nursingHome: {
      appliesToMe: ltcPreferencesData?.nursingHomeSelectedOptions ? ltcPreferencesData.nursingHomeSelectedOptions.length > 0 : true,
      selectedOptions: ltcPreferencesData?.nursingHomeSelectedOptions || [],
      additionalPreferences: ltcPreferencesData?.nursingHomeAdditionalPreferences || ''
    },
    hospice: {
      appliesToMe: ltcPreferencesData?.hospiceSelectedOptions ? ltcPreferencesData.hospiceSelectedOptions.length > 0 : true,
      selectedOptions: ltcPreferencesData?.hospiceSelectedOptions || [],
      additionalPreferences: ltcPreferencesData?.hospiceAdditionalPreferences || ''
    },
    respiteCare: {
      appliesToMe: ltcPreferencesData?.respiteCareSelectedOptions ? ltcPreferencesData.respiteCareSelectedOptions.length > 0 : true,
      selectedOptions: ltcPreferencesData?.respiteCareSelectedOptions || [],
      additionalPreferences: ltcPreferencesData?.respiteCareAdditionalPreferences || ''
    },
    continuedCare: {
      appliesToMe: ltcPreferencesData?.continuedCareSelectedOptions ? ltcPreferencesData.continuedCareSelectedOptions.length > 0 : true,
      selectedOptions: ltcPreferencesData?.continuedCareSelectedOptions || [],
      additionalPreferences: ltcPreferencesData?.continuedCareAdditionalPreferences || ''
    },
  });
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const toggleAppliesToMe = (sectionKey: string) => {
    setPreferences(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey as keyof LocalLTCPreferencesData],
        appliesToMe: !prev[sectionKey as keyof LocalLTCPreferencesData].appliesToMe,
        // Clear selections if turning off
        selectedOptions: !prev[sectionKey as keyof LocalLTCPreferencesData].appliesToMe
          ? prev[sectionKey as keyof LocalLTCPreferencesData].selectedOptions
          : [],
        additionalPreferences: !prev[sectionKey as keyof LocalLTCPreferencesData].appliesToMe
          ? prev[sectionKey as keyof LocalLTCPreferencesData].additionalPreferences
          : '',
      },
    }));
  };

  const togglePreference = (sectionKey: string, optionId: string) => {
    setPreferences(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey as keyof LocalLTCPreferencesData],
        selectedOptions: prev[sectionKey as keyof LocalLTCPreferencesData].selectedOptions.includes(optionId)
          ? prev[sectionKey as keyof LocalLTCPreferencesData].selectedOptions.filter(id => id !== optionId)
          : [...prev[sectionKey as keyof LocalLTCPreferencesData].selectedOptions, optionId],
      },
    }));
  };

  const updateAdditionalPreferences = (sectionKey: string, text: string) => {
    setPreferences(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey as keyof LocalLTCPreferencesData],
        additionalPreferences: text,
      },
    }));
  };

  const getSectionCompletion = () => {
    const sections = Object.keys(PREFERENCES_DATA);
    const completed = sections.filter(key => {
      const pref = preferences[key as keyof LocalLTCPreferencesData];
      return !pref.appliesToMe || pref.selectedOptions.length > 0 || pref.additionalPreferences.trim().length > 0;
    });
    return { completed: completed.length, total: sections.length };
  };

  const isSectionComplete = (sectionKey: string) => {
    const pref = preferences[sectionKey as keyof LocalLTCPreferencesData];
    return !pref.appliesToMe || pref.selectedOptions.length > 0 || pref.additionalPreferences.trim().length > 0;
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const ltcPreferencesUpdateData: Partial<LTCPreferencesData> = {
        inHomeCareSelectedOptions: preferences.inHomeCare.appliesToMe ? preferences.inHomeCare.selectedOptions : [],
        inHomeCareAdditionalPreferences: preferences.inHomeCare.appliesToMe ? preferences.inHomeCare.additionalPreferences : '',
        adultDaycareSelectedOptions: preferences.adultDaycare.appliesToMe ? preferences.adultDaycare.selectedOptions : [],
        adultDaycareAdditionalPreferences: preferences.adultDaycare.appliesToMe ? preferences.adultDaycare.additionalPreferences : '',
        assistedLivingSelectedOptions: preferences.assistedLiving.appliesToMe ? preferences.assistedLiving.selectedOptions : [],
        assistedLivingAdditionalPreferences: preferences.assistedLiving.appliesToMe ? preferences.assistedLiving.additionalPreferences : '',
        memoryCareSelectedOptions: preferences.memoryCare.appliesToMe ? preferences.memoryCare.selectedOptions : [],
        memoryCareAdditionalPreferences: preferences.memoryCare.appliesToMe ? preferences.memoryCare.additionalPreferences : '',
        nursingHomeSelectedOptions: preferences.nursingHome.appliesToMe ? preferences.nursingHome.selectedOptions : [],
        nursingHomeAdditionalPreferences: preferences.nursingHome.appliesToMe ? preferences.nursingHome.additionalPreferences : '',
        hospiceSelectedOptions: preferences.hospice.appliesToMe ? preferences.hospice.selectedOptions : [],
        hospiceAdditionalPreferences: preferences.hospice.appliesToMe ? preferences.hospice.additionalPreferences : '',
        respiteCareSelectedOptions: preferences.respiteCare.appliesToMe ? preferences.respiteCare.selectedOptions : [],
        respiteCareAdditionalPreferences: preferences.respiteCare.appliesToMe ? preferences.respiteCare.additionalPreferences : '',
        continuedCareSelectedOptions: preferences.continuedCare.appliesToMe ? preferences.continuedCare.selectedOptions : [],
        continuedCareAdditionalPreferences: preferences.continuedCare.appliesToMe ? preferences.continuedCare.additionalPreferences : '',
        doesNotApply: false,
      };

      let savedLTCPreferences;

      if (isEditing && ltcPreferencesData?.id) {
        // Update existing record
        savedLTCPreferences = await ltcPreferencesService.updateLTCPreferences(
          ltcPreferencesData.id,
          ltcPreferencesUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedLTCPreferences?.data || savedLTCPreferences;
        logger.debug('LTC preferences updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('LTCPreferencesLockUnlock', { ltcPreferencesId: updatedData.id });
      } else {
        // Create new record
        savedLTCPreferences = await ltcPreferencesService.createLTCPreferences(ltcPreferencesUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedLTCPreferences?.data || savedLTCPreferences;
        logger.debug('LTC preferences saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('LTCPreferencesLockUnlock', { ltcPreferencesId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving LTC preferences:', error);
      Alert.alert('Error', 'Failed to save LTC preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderPreferenceSection = (sectionKey: string) => {
    const sectionData = PREFERENCES_DATA[sectionKey as keyof typeof PREFERENCES_DATA];
    const sectionPreferences = preferences[sectionKey as keyof LocalLTCPreferencesData];
    const isExpanded = expandedSections[sectionKey];
    const isComplete = isSectionComplete(sectionKey);

    return (
      <View key={sectionKey} style={styles.sectionContainer}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => toggleSection(sectionKey)}
        >
          <View style={styles.sectionHeaderLeft}>
            <Text style={styles.sectionTitle}>{sectionData.title}</Text>
            {isComplete && <Text style={styles.completionBadge}>✓</Text>}
          </View>
          <Image
            source={require('../../../../assets/icons/down.png')}
            style={[
              styles.chevronIcon,
              { transform: [{ rotate: isExpanded ? '180deg' : '0deg' }] }
            ]}
          />
        </TouchableOpacity>

        {isExpanded && (
          <View style={styles.sectionContent}>
            {/* Description */}
            <Text style={styles.sectionDescription}>{sectionData.description}</Text>

            {/* "This applies to me" toggle */}
            <View style={styles.appliesToMeContainer}>
              <Text style={styles.appliesToMeLabel}>I want to share preferences for this care type</Text>
              <Switch
                value={sectionPreferences.appliesToMe}
                onValueChange={() => toggleAppliesToMe(sectionKey)}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={sectionPreferences.appliesToMe ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {sectionPreferences.appliesToMe && (
              <>
                {/* Options with proper checkboxes */}
                <View style={styles.optionsContainer}>
                  <Text style={styles.optionsTitle}>Select your preferences:</Text>
                  {sectionData.options.map((option) => {
                    const isSelected = sectionPreferences.selectedOptions.includes(option.id);
                    return (
                      <TouchableOpacity
                        key={option.id}
                        style={styles.optionContainer}
                        onPress={() => togglePreference(sectionKey, option.id)}
                      >
                        <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                          {isSelected && <Text style={styles.checkmark}>✓</Text>}
                        </View>
                        <Text style={styles.optionText}>{option.label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {/* Additional preferences */}
                <View style={styles.additionalPreferencesContainer}>
                  <Text style={styles.additionalPreferencesLabel}>Additional Preferences or Notes:</Text>
                  <TextInput
                    style={styles.additionalPreferencesInput}
                    placeholder="Add any specific preferences, requirements, or concerns not listed above..."
                    placeholderTextColor="#A9A9A9"
                    value={sectionPreferences.additionalPreferences}
                    onChangeText={(text) => updateAdditionalPreferences(sectionKey, text)}
                    multiline
                    numberOfLines={3}
                    textAlignVertical="top"
                  />
                </View>
              </>
            )}

            {!sectionPreferences.appliesToMe && (
              <Text style={styles.notApplicableText}>
                This care type does not apply to your situation or you prefer not to specify preferences at this time.
              </Text>
            )}
          </View>
        )}
      </View>
    );
  };

  const completion = getSectionCompletion();

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Long-Term Care Preferences</Text>
        <Text style={styles.subtitle}>
          Help your loved ones understand your care wishes
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          {/* Progress Summary */}
          <View style={styles.progressContainer}>
            <Text style={styles.progressText}>
              Progress: {completion.completed} of {completion.total} sections reviewed
            </Text>
            <View style={styles.progressBarBackground}>
              <View
                style={[
                  styles.progressBarFill,
                  { width: `${(completion.completed / completion.total) * 100}%` }
                ]}
              />
            </View>
            <Text style={styles.progressHint}>
              Tap each section to expand and set your preferences
            </Text>
          </View>

          {/* Sections */}
          {Object.keys(PREFERENCES_DATA).map(renderPreferenceSection)}

          {/* Submit Button */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.submitButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.submitButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: ms(14, 0.3),
    color: '#FFFFFF',
    opacity: 0.9,
    textAlign: 'center',
    paddingHorizontal: ms(30),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  progressContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(16),
    marginBottom: ms(20),
  },
  progressText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  progressBarBackground: {
    height: s(8),
    backgroundColor: '#E5E7EB',
    borderRadius: ms(4),
    overflow: 'hidden',
    marginBottom: ms(8),
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#29BFC0',
    borderRadius: ms(4),
  },
  progressHint: {
    fontSize: ms(13, 0.3),
    color: '#6B7280',
    fontStyle: 'italic',
  },
  sectionContainer: {
    marginBottom: ms(16),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    overflow: 'hidden',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    backgroundColor: '#FFFFFF',
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginRight: ms(8),
  },
  completionBadge: {
    fontSize: ms(16, 0.3),
    color: '#10B981',
    fontWeight: 'bold',
  },
  chevronIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  sectionContent: {
    padding: ms(16),
    paddingTop: 0,
  },
  sectionDescription: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(16),
    fontStyle: 'italic',
  },
  appliesToMeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: ms(12),
    borderRadius: ms(10),
    marginBottom: ms(16),
  },
  appliesToMeLabel: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    flex: 1,
    marginRight: ms(10),
  },
  optionsContainer: {
    marginBottom: ms(16),
  },
  optionsTitle: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(12),
  },
  optionContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(12),
    paddingVertical: ms(4),
  },
  checkbox: {
    width: s(22),
    height: s(22),
    borderRadius: ms(4),
    borderWidth: ms(2),
    borderColor: '#29BFC0',
    marginRight: ms(12),
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkboxSelected: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  optionText: {
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    flex: 1,
    lineHeight: ms(20, 0.3),
  },
  additionalPreferencesContainer: {
    marginTop: ms(8),
  },
  additionalPreferencesLabel: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  additionalPreferencesInput: {
    backgroundColor: '#F9FAFB',
    borderRadius: ms(10),
    padding: ms(12),
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#E5E7EB',
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  notApplicableText: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: ms(20),
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});
